import { useQuery } from '@tanstack/react-query';
import { apiService } from '../services/api';
import { Customer, FlightType, OrgMember, Payload, Pilot, Project } from '../types/api';

// STD-015: server state for the flight-metadata-options domain. apiService is
// imported here (a *.queries.ts file) per STD-016. The query opts out of the
// global error toast because callers surface an inline `error` string instead —
// consistent with the previous hook behaviour.

export interface FlightMetadataOptionsData {
  flightTypes: FlightType[];
  payloads: Payload[];
  pilots: Pilot[];
  customers: Customer[];
  projects: Project[];
  airspaceObservers: OrgMember[];
  payloadOperators: OrgMember[];
}

export const flightMetadataOptionsKeys = {
  all: ['flightMetadataOptions'] as const,
  options: () => [...flightMetadataOptionsKeys.all, 'options'] as const,
};

export function useFlightMetadataOptionsQuery({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: flightMetadataOptionsKeys.options(),
    enabled,
    meta: { skipGlobalErrorToast: true },
    queryFn: async (): Promise<FlightMetadataOptionsData> => {
      const [
        flightTypes,
        payloads,
        pilots,
        customers,
        projects,
        airspaceObservers,
        payloadOperators,
      ] = await Promise.all([
        apiService.getFlightTypesFromDB(),
        apiService.getPayloads(),
        apiService.getPilots(),
        apiService.getCustomers(),
        apiService.getProjects(),
        apiService.getAirspaceObservers(),
        apiService.getPayloadOperators(),
      ]);

      return {
        flightTypes,
        payloads,
        pilots,
        customers,
        projects,
        airspaceObservers,
        payloadOperators,
      };
    },
  });
}
